import { ReactComponent as BookingsIcon } from '@/assets/icons/bookings_icon.svg';
import { Text, VSpacer } from '@/components/DesignSystem';
import { ResolvedOrderTransaction } from '@/pages/CustomerDashboard/interfaces';
import { ApiUser } from '@api/interfaces';
import { ApiPointsHistory } from '@api/interfaces/ApiPointsHistory';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Divider, Stack, Tooltip, useTheme } from '@mui/material';
import { OrderTransactionSubtype, OrderTransactionType, TransactionType } from '@shared/enums';
import { formatCurrency, formatNumber } from '@shared/utilities';
import { getPointsEntryLabel } from '@/utilities/OrderLabels';
import { getMostRecentDescription, getPointsTotalsByType } from '@shared/utilities/PointsUtils';
import { getAccountName } from '@shared/utilities/UserUtilities';
import i18next from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type PointsActivityItemProps = {
  brandColor: string,
  farmer: ApiUser,
  linkedAccounts: ApiUser[],
  onClick?: () => void,
} & (
  | { order: ResolvedOrderTransaction; pointsHistory?: never }
  | { order?: never; pointsHistory: ApiPointsHistory }
);

export const PointsActivityItem = ({
  brandColor,
  farmer,
  linkedAccounts,
  onClick,
  order,
  pointsHistory,
}: PointsActivityItemProps) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const isManuallyIssued = !order;

  const farmerId = isManuallyIssued ? pointsHistory.userId : order.farmerId;

  const customer = useMemo(() => {
    if (linkedAccounts.length > 0) {
      const account = linkedAccounts.find((a) => a.id === farmerId);
      return account || farmer;
    }
    return farmer;
  }, [farmer, linkedAccounts, farmerId]);

  const isAssociatedWithBooking = useMemo(() => {
    return order?.products?.some((product) => product.linkedBookingId) ?? false;
  }, [order?.products]);

  const transactionTypeLabel = useMemo(() => {
    if (isManuallyIssued) {
      return t($ => $.customerDashboard.pointsActivity.adjustment);
    }
    if (order.orderType === OrderTransactionType.INVOICE) {
      if (order.orderSubtype === OrderTransactionSubtype.SCALE_TICKET) {
        return t($ => $.customerDashboard.pointsActivity.filters.scaleTicket);
      }
      if (order.orderSubtype === OrderTransactionSubtype.SETTLEMENT) {
        return t($ => $.common.order.settlement);
      }
    }
    return t($ => $.common.order.order);
  }, [isManuallyIssued, order?.orderType, order?.orderSubtype, t]);

  const formattedUom = useMemo(() => {
    if (isManuallyIssued) return null;
    const isSettlementOrScaleTicket = order.orderType === OrderTransactionType.INVOICE &&
      (order.orderSubtype === OrderTransactionSubtype.SETTLEMENT ||
       order.orderSubtype === OrderTransactionSubtype.SCALE_TICKET);
    if (!isSettlementOrScaleTicket) {
      return null;
    }
    const total = order.products?.reduce((sum, product) => {
      return sum + (product.quantity ?? 0);
    }, 0) ?? 0;
    if (total === 0) {
      return null;
    }
    const formatted = formatNumber(total, i18next, { maximumFractionDigits: 4 });
    return `${formatted} ${t(($) => $.customerDashboard.pointsActivity.bu)}`;
  }, [isManuallyIssued, order?.orderType, order?.orderSubtype, order?.products]);

  const { pointsEarned, pointsRedeemed, pointsRefunded, pointsReturned } = useMemo(() => {
    const histories: { amount: number, type: TransactionType }[] = isManuallyIssued
      ? [{ amount: pointsHistory.amount, type: pointsHistory.type }]
      : (order.pointHistories ?? []);
    const totals = getPointsTotalsByType(histories);
    return {
      pointsEarned: totals.earned,
      pointsRedeemed: totals.redeemed,
      pointsRefunded: totals.refunded,
      pointsReturned: totals.returned,
    };
  }, [
    isManuallyIssued,
    order?.pointHistories,
    pointsHistory?.amount,
    pointsHistory?.type,
  ]);

  const manualAdjustmentDisplay = useMemo(() => {
    if (!isManuallyIssued) {
      return null;
    }
    return {
      amount: pointsHistory.amount,
      label: getPointsEntryLabel(pointsHistory.type, t),
    };
  }, [isManuallyIssued, pointsHistory?.amount, pointsHistory?.type, t]);

  const mostRecentDescription = useMemo(() => {
    if (isManuallyIssued) return pointsHistory.description ?? null;
    return getMostRecentDescription(order.pointHistories ?? []);
  }, [isManuallyIssued, order?.pointHistories, pointsHistory?.description]);

  const showAccountName = linkedAccounts.length > 0;
  const showUom = !isManuallyIssued && formattedUom !== null &&
    order.orderType === OrderTransactionType.INVOICE &&
    (order.orderSubtype === OrderTransactionSubtype.SETTLEMENT ||
     order.orderSubtype === OrderTransactionSubtype.SCALE_TICKET);

  return (
      <Stack
        bgcolor="white"
        borderRadius="8px"
        data-testid="points-activity-card"
        direction="row"
        justifyContent="space-between"
        onClick={onClick}
        p="16px"
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          cursor: onClick ? 'pointer' : 'default',
        }}
      >
        <Stack flex={1} minWidth={0}>
          <Stack alignItems="center" direction="row" gap="4px" sx={{ flexWrap: 'wrap' }}>
            <Text
              breakWord
              sx={{
                color: theme.palette.text.primary,
                fontSize: '17px',
                fontWeight: 500,
              }}
              testID="points-activity-transaction"
            >
              {transactionTypeLabel}{!isManuallyIssued && ` ${order.externalId}`}
              {isAssociatedWithBooking && (
                <BookingsIcon
                  style={{
                    color: brandColor ?? theme.palette.primary.main,
                    height: '16px',
                    width: '16px',
                    marginBottom: '2px',
                    marginLeft: '4px',
                    verticalAlign: 'middle',
                  }}
                />
              )}
            </Text>
          </Stack>
          <VSpacer size="2" />
          <VSpacer size="1" />
          {!isManuallyIssued && (
            <Stack alignItems="center" direction="row" gap="8px">
              <Text
                category="label-medium"
                sx={{ color: theme.palette.text.secondary }}
                testID="points-activity-total"
              >
                {t($ => $.common.order.total)}: {formatCurrency(order.totalCost, i18n)}
              </Text>
              {showUom && (
                <>
                  <Text category="label-medium" sx={{ color: theme.palette.text.secondary }}>
                    |
                  </Text>
                  <Text category="label-medium" sx={{ color: theme.palette.text.secondary }}>
                    {formattedUom}
                  </Text>
                </>
              )}
            </Stack>
          )}
          {showAccountName && (
            <>
              <VSpacer size="2" />
              <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
                {getAccountName(customer)}
              </Text>
            </>
          )}
          <VSpacer size="3" />
          {!!mostRecentDescription && (
            <>
              <Text category="body-small">
                {t($ => $.customers.transactions.description)}
              </Text>
              <Tooltip title={mostRecentDescription.length > 50 ? mostRecentDescription : ''}>
                <span>
                  <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
                    {mostRecentDescription.length > 50
                      ? `${mostRecentDescription.slice(0, 50)}…`
                      : mostRecentDescription}
                  </Text>
                </span>
              </Tooltip>
              <VSpacer size="2" />
            </>
          )}
          {manualAdjustmentDisplay ? (
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <Text
                category="body-large"
                sx={{ color: theme.palette.text.primary }}
                testID="points-activity-label"
              >
                {manualAdjustmentDisplay.label}
              </Text>
              <Text
                category="title-medium"
                sx={{
                  color: manualAdjustmentDisplay.amount >= 0
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                }}
                testID="points-activity-value"
              >
                {manualAdjustmentDisplay.amount >= 0 ? '+' : '-'}
                {formatNumber(Math.abs(manualAdjustmentDisplay.amount), i18n)}
              </Text>
            </Stack>
          ) : (
            <>
              {pointsEarned !== 0 && (
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Text
                    category="body-large"
                    sx={{ color: theme.palette.text.primary }}
                    testID="points-activity-label"
                  >
                    {t(($) => $.customerDashboard.pointsActivity.pointsEarned)}
                  </Text>
                  <Text
                    category="title-medium"
                    sx={{ color: theme.palette.success.main }}
                    testID="points-activity-value"
                  >
                    +{formatNumber(pointsEarned, i18n)}
                  </Text>
                </Stack>
              )}
              {pointsRedeemed !== 0 && (
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Text category="body-large" sx={{ color: theme.palette.text.primary }}>
                    {t($ => $.bookings.filters.pointsRedeemed)}
                  </Text>
                  <Text category="title-medium" sx={{ color: theme.palette.error.main }}>
                    -{formatNumber(pointsRedeemed, i18n)}
                  </Text>
                </Stack>
              )}
              {pointsRefunded !== 0 && (
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Text category="body-large" sx={{ color: theme.palette.text.primary }}>
                    {t($ => $.bookings.filters.pointsRefunded)}
                  </Text>
                  <Text category="title-medium" sx={{ color: theme.palette.error.main }}>
                    -{formatNumber(pointsRefunded, i18n)}
                  </Text>
                </Stack>
              )}
              {pointsReturned !== 0 && (
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Text category="body-large" sx={{ color: theme.palette.text.primary }}>
                    {t($ => $.bookings.filters.pointsReturned)}
                  </Text>
                  <Text category="title-medium" sx={{ color: theme.palette.error.main }}>
                    -{formatNumber(pointsReturned, i18n)}
                  </Text>
                </Stack>
              )}
            </>
          )}
        </Stack>
        <Divider flexItem orientation="vertical" sx={{ mx: '16px' }} />
        <Stack
          alignItems="center"
          data-testid="points-activity-expand"
          justifyContent="center"
          sx={{ cursor: 'pointer', flexShrink: 0, width: '56px' }}
        >
          <ExpandMore sx={{ color: theme.palette.text.secondary }} />
        </Stack>
      </Stack>
  );
};
